export const CHECKLIST_FORM_KEYS = ['florida', 'wyoming', 'newJersey', 'delawareLLC', 'delawareCCorp'];

const field = (key, label, opts = {}) => ({
  key,
  label,
  type: opts.type || 'text',
  multiline: !!opts.multiline,
  fullWidth: !!opts.fullWidth,
  required: !!opts.required,
  order: opts.order ?? 0,
  isActive: opts.isActive !== false,
});

const fieldsSection = (title, order, fields) => ({
  type: 'fields',
  title,
  order,
  isActive: true,
  fields,
});

const directorListSection = (title, order, key, fields, opts = {}) => ({
  type: 'directorList',
  key,
  title,
  order,
  isActive: true,
  addLabel: opts.addLabel || 'Add Director',
  itemLabelPrefix: opts.itemLabelPrefix || 'Director',
  minItems: opts.minItems ?? 1,
  defaultItemCount: opts.defaultItemCount ?? 1,
  fields,
});

const selectSection = (title, order, key, label, options) => ({
  type: 'select',
  title,
  order,
  isActive: true,
  key,
  label,
  options,
});

export const DEFAULT_CHECKLIST_SHELL = {
  backLabel: 'Back',
  saveDraftLabel: 'Save Draft',
  submitLabel: 'Submit Checklist',
};

export const DEFAULT_ANNUAL_REPORT_CHECKLISTS = {
  florida: {
    title: 'Florida Annual Report Checklist',
    subtitle: 'Complete the business and director details below.',
    sections: [
      fieldsSection('(A) Basic Business Details', 0, [
        field('businessName', 'Business Name', { required: true }),
        field('einNumber', 'EIN Number'),
        field('principalAddress', 'Principal Address', { multiline: true, fullWidth: true }),
        field('mailingAddress', 'Mailing Address', { multiline: true, fullWidth: true }),
        field('registeredAgent', 'Registered Agent'),
        field('registeredAgentAddress', 'Registered Agent Address', { multiline: true, fullWidth: true }),
      ]),
      fieldsSection('(B) Director Details', 1, [
        field('director1Name', 'Director 1 Name'),
        field('director1Address', 'Director 1 Address', { multiline: true }),
        field('director2Name', 'Director 2 Name'),
        field('director2Address', 'Director 2 Address', { multiline: true }),
        field('director3Name', 'Director 3 Name'),
        field('director3Address', 'Director 3 Address', { multiline: true }),
      ]),
      fieldsSection('Email and Certification', 2, [
        field('emailAddress', 'Email Address', { type: 'email' }),
        field('certifyName', 'Certify Name'),
        field('certifyTitle', 'Title'),
      ]),
    ],
  },
  wyoming: {
    title: 'Wyoming Annual Report Checklist',
    subtitle: 'Complete mailing, office, asset worksheet, and certification details.',
    sections: [
      fieldsSection('(A) Mailing Address', 0, [
        field('mailingCountry', 'Country'),
        field('mailingAddr1', 'Address Line 1'),
        field('mailingAddr2', 'Address Line 2'),
        field('mailingCity', 'City'),
        field('mailingState', 'State'),
        field('mailingPostalCode', 'Postal Code'),
      ]),
      fieldsSection('(B) Principal Office Address', 1, [
        field('principalCountry', 'Country'),
        field('principalAddr1', 'Address Line 1'),
        field('principalAddr2', 'Address Line 2'),
        field('principalCity', 'City'),
        field('principalState', 'State'),
        field('principalPostalCode', 'Postal Code'),
        field('principalPhone', 'Phone'),
        field('principalEmail', 'Email', { type: 'email' }),
      ]),
      fieldsSection('(C) Asset Worksheet & Fees', 2, [
        field('cash', 'Cash', { type: 'number' }),
        field('tradeNotes', 'Trade Notes & Accounts Receivable', { type: 'number' }),
        field('subtractAllowance', 'Subtract Allowance for Bad Debts', { type: 'number' }),
        field('inventories', 'Inventories', { type: 'number' }),
        field('usGovObligations', 'U.S. Government Obligations', { type: 'number' }),
        field('taxExemptSecurities', 'Tax Exempt Securities', { type: 'number' }),
        field('otherCurrentAssets', 'Other Current Assets', { type: 'number' }),
        field('loansToStockholders', 'Loans to Stockholders', { type: 'number' }),
        field('mortgageRealEstateLoans', 'Mortgage and Real Estate Loans', { type: 'number' }),
        field('otherInvestments', 'Other Investments', { type: 'number' }),
        field('depreciableAssets', 'Depreciable Assets', { type: 'number' }),
        field('depletableAssets', 'Depletable Assets', { type: 'number' }),
        field('land', 'Land (Assessed Valuation)', { type: 'number' }),
        field('intangibleAssets', 'Intangible Assets', { type: 'number' }),
        field('subtractAmortization', 'Subtract Accumulated Amortization', { type: 'number' }),
        field('otherAssets', 'Other Assets', { type: 'number' }),
        field('totalAssetValue', 'Total Asset Value', { type: 'number' }),
        field('filingFee', 'Final Filing Fee', { type: 'number' }),
      ]),
      fieldsSection('(D) Certification', 3, [
        field('certifyFirstName', 'First Name'),
        field('certifyMiddleName', 'Middle Name'),
        field('certifyLastName', 'Last Name'),
        field('certifyEmail', 'Email', { type: 'email' }),
      ]),
    ],
  },
  newJersey: {
    title: 'New Jersey Annual Report Checklist',
    subtitle: 'Complete registered agent, director, and authorization details.',
    sections: [
      fieldsSection('(A) Registered Agent and Office', 0, [
        field('raName', 'Registered Agent'),
        field('raNumber', 'Registered Agent #'),
        field('raAddr1', 'Address Line 1'),
        field('raAddr2', 'Address Line 2'),
        field('raCityStateZip', 'City, State, Zip', { fullWidth: true }),
      ]),
      fieldsSection('(B) Business Address Change', 1, [
        field('businessAddressChange', 'Business Address Change', { multiline: true, fullWidth: true }),
      ]),
      directorListSection('(C) List of Directors', 2, 'directors', [
        field('name', 'Name'),
        field('title', 'Title'),
        field('address', 'Address', { multiline: true, fullWidth: true }),
      ], { defaultItemCount: 3 }),
      selectSection("(D) Workers' Compensation", 3, 'workersCompRequired', "Required to provide worker's compensation insurance?", [
        { value: '', label: 'Select' },
        { value: 'Yes', label: 'Yes' },
        { value: 'No', label: 'No' },
      ]),
      fieldsSection('(E) Authorization', 4, [
        field('authName', 'Authorizer Name'),
        field('authTitle', 'Title'),
        field('authPhone', 'Phone Number'),
        field('authAddress', 'Address', { multiline: true, fullWidth: true }),
      ]),
    ],
  },
  delawareLLC: {
    title: 'Delaware LLC Annual Report Checklist',
    subtitle: 'Complete company and registered agent details.',
    sections: [
      fieldsSection('Checklist Details', 0, [
        field('companyName', 'Company Name', { required: true }),
        field('taxYear', 'Tax Year'),
        field('raName', 'Agent Name'),
        field('raAddress', 'Agent Address', { multiline: true, fullWidth: true }),
        field('raCity', 'City'),
        field('raState', 'State'),
        field('raZipCode', 'Zip Code'),
      ]),
    ],
  },
  delawareCCorp: {
    title: 'Delaware C-CORP Annual Report Checklist',
    subtitle: 'Complete principal place of business, officer, director, and authorization details.',
    sections: [
      fieldsSection('1. Principal Place of Business', 0, [
        field('fiscalYearEnd', 'Fiscal Year End'),
        field('principalStreet', 'Street Address'),
        field('principalCity', 'City'),
        field('principalState', 'State'),
        field('principalZip', 'Zip'),
        field('principalCountry', 'Country'),
        field('principalPhone', 'Phone'),
        field('principalEmail', 'Email', { type: 'email' }),
        field('natureOfBusiness', 'Nature of Business', { multiline: true, fullWidth: true }),
      ]),
      fieldsSection('2. Officer Information', 1, [
        field('officerFirstName', 'First Name'),
        field('officerMiddleName', 'Middle Name'),
        field('officerLastName', 'Last Name'),
        field('officerTitle', 'Title'),
        field('officerStreet', 'Street'),
        field('officerCity', 'City'),
        field('officerState', 'State'),
        field('officerZip', 'Zip'),
        field('officerCountry', 'Country'),
      ]),
      directorListSection('3. Directors Information', 2, 'directors', [
        field('firstName', 'First Name'),
        field('middleName', 'Middle Name'),
        field('lastName', 'Last Name'),
        field('street', 'Street'),
        field('city', 'City'),
        field('state', 'State'),
        field('zip', 'Zip'),
        field('country', 'Country'),
      ]),
      fieldsSection('4. Authorization Person', 3, [
        field('authFirstName', 'First Name'),
        field('authMiddleName', 'Middle Name'),
        field('authLastName', 'Last Name'),
        field('authTitle', 'Title'),
        field('authStreet', 'Street'),
        field('authCity', 'City'),
        field('authState', 'State'),
        field('authZip', 'Zip'),
        field('authCountry', 'Country'),
      ]),
    ],
  },
};

export const CHECKLIST_FORM_OPTIONS = [
  { key: 'florida', label: 'Florida (Default)' },
  { key: 'wyoming', label: 'Wyoming' },
  { key: 'newJersey', label: 'New Jersey' },
  { key: 'delawareLLC', label: 'Delaware LLC' },
  { key: 'delawareCCorp', label: 'Delaware C-Corp' },
];

const mergeSections = (defaults = [], override = []) =>
  (override.length ? override : defaults).map((section, index) => {
    const base = defaults[index] || {};
    const merged = { ...base, ...section };
    if (merged.fields) {
      merged.fields = merged.fields.map((f, fi) => ({ ...base.fields?.[fi], ...f }));
    }
    return merged;
  });

export const mergeChecklistConfig = (defaults, override) => {
  if (!defaults) return override || null;
  if (!override) return defaults;
  return {
    ...defaults,
    ...override,
    sections: mergeSections(defaults.sections, override.sections),
  };
};

export const mergeAllChecklists = (contentChecklists = {}) => {
  const merged = {};
  CHECKLIST_FORM_KEYS.forEach((key) => {
    merged[key] = mergeChecklistConfig(
      DEFAULT_ANNUAL_REPORT_CHECKLISTS[key],
      contentChecklists[key]
    );
  });
  return merged;
};

export const resolveAnnualReportChecklistKey = (service) => {
  if (service?.state === 'Wyoming') return 'wyoming';
  if (service?.state === 'New Jersey') return 'newJersey';
  if (service?.state === 'Delaware') {
    return service?.entityType === 'LLC' ? 'delawareLLC' : 'delawareCCorp';
  }
  return 'florida';
};

export const buildChecklistDefaultValues = (checklist) => {
  const values = {};
  (checklist?.sections || [])
    .filter((section) => section.isActive !== false)
    .forEach((section) => {
      if (section.type === 'fields') {
        section.fields?.filter((f) => f.isActive !== false).forEach((f) => {
          values[f.key] = '';
        });
      }
      if (section.type === 'select' && section.key) {
        values[section.key] = '';
      }
      if (section.type === 'directorList' && section.key) {
        const count = section.defaultItemCount || 1;
        const emptyItem = {};
        section.fields?.forEach((f) => {
          emptyItem[f.key] = '';
        });
        values[section.key] = Array.from({ length: count }, () => ({ ...emptyItem }));
      }
    });
  return values;
};

export const getChecklistProgressFields = (checklist) => {
  const fields = [];
  (checklist?.sections || [])
    .filter((section) => section.isActive !== false)
    .forEach((section) => {
      if (section.type === 'fields') {
        section.fields
          ?.filter((f) => f.isActive !== false)
          .forEach((f) => fields.push(f.key));
      }
      if (section.type === 'select' && section.key) {
        fields.push(section.key);
      }
    });
  return fields;
};
